/**
 * Merchant Register Data
 * Contains types, interfaces, and default values for merchant registration
 */

// ============================================
// Types & Interfaces
// ============================================

/**
 * Step button configuration
 */
export interface StepButton {
  label: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Merchant registration step configuration
 */
export interface MerchantStep {
  id: string;
  title: string;
  previousBtn?: StepButton;
  nextBtn?: StepButton;
}

/**
 * Address data for business locations
 */
export interface BusinessAddress {
  address1: string;
  city: string;
  state: string;
  zipcode: string;
  latitude?: number;
  longitude?: number;
}

/** Account information submitted by Step 1. */
export interface MerchantAccountFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

/** Email verification data submitted by Step 2. */
export interface MerchantEmailVerificationFormData {
  email: string;
  otp: string;
}

/** Partner types accepted by the merchant creation workflow. */
export enum PartnerType {
  None = "",
  CitiesMunicipalities = "cities_municipalities",
  LocalBusinesses = "local_businesses",
  Corporate = "corporate",
  NonprofitsCommunityOrganizations = "nonprofits_community_organizations",
}

export interface MerchantBusinessFormData {
  logoImage: File | null;
  businessName: string;
  businessShortIntro: string;
  description?: string;
  businessCategory: string;
  partnerType: PartnerType;
  businessUrl?: string;
  addresses: BusinessAddress[];
}

export interface MerchantBusinessProfileResponse {
  id: string;
  logo_url?: string | null;
  business_name?: string | null;
  short_intro?: string | null;
  description?: string | null;
  business_url?: string | null;
  business_category_id?: string | null;
  partner_type?: string | null;
}

/**
 * Social links for merchant
 */
export interface SocialLinks {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  tiktok?: string;
}

/**
 * Location request for registration
 */
export interface LocationRequest {
  address: string;
  city: string;
  state: string;
  zip_code: string;
  organization_id: string;
  latitude?: number;
  longitude?: number;
}

/**
 * Request to register local merchant (backend format - snake_case)
 */
export interface RegisterLocalMerchantRequest {
  // Account Info
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
  phone_number: string;

  // Business Info
  logo_base64?: string;
  business_name: string;
  short_intro: string;
  description?: string;
  business_url?: string;
  business_category_id: string;
  social_links?: SocialLinks;

  // Locations
  locations: LocationRequest[];
}

/**
 * Location response from registration
 */
export interface LocationResponse {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
}

/**
 * Merchant response from registration
 */
export interface LocalMerchantResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  logo_url: string;
  business_name: string;
  short_intro: string;
  description?: string;
  business_url?: string;
  business_category_id: string;
  social_links?: SocialLinks;
  created_at: string;
  updated_at: string;
}

/**
 * Response from register local merchant API
 */
export interface RegisterLocalMerchantResponse {
  merchant: LocalMerchantResponse;
  locations: LocationResponse[];
  access_token: string;
  refresh_token: string;
}

/**
 * Merchant Category from API response
 */
export interface MerchantCategory {
  id: string;
  icon_url: string | null;
  order_number: number;
  brand_keys: string[];
  brand_names: string[];
  brand_ids: string[];
  name: string;
  hover_icon_url: string | null;
}

/**
 * Merchant Category option for dropdown
 */
export interface MerchantCategoryOption {
  value: string;
  label: string;
}

/** Public merchant-category response returned during registration. */
export interface CategoryResponse {
  id: string;
  name: string;
  type: string;
  icon_url?: string | null;
  icon_hover_url?: string;
}

export interface MerchantRegistrationCityOption {
  value: string;
  label: string;
  state: string;
}

/**
 * Organization (City) from API response
 */
export interface Organization {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  name: string;
  state: string;
  organization_type: string;
  location_name: string;
}

/**
 * Merchant Form Context Value
 */
